// Admin → Subscriptions. Who is on what plan, until when, and why. M7c / P4.
//
// The counterpart to Payments: that view is invoice-shaped (money that moved),
// this one is customer-shaped (access that is granted). Both drill into the
// same records from opposite directions.
//
// 🔴 A comp grant is an ORDINARY subscription — an `active` row with a real
// period end and `metadata.comp`. It expires through the same path as a paid
// one. The `Comp` badge here is a LABEL, never a behaviour switch.

import { Button, Input } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  ConfirmDialog,
  DataTable,
  DetailRow,
  DetailSection,
  Drawer,
  FilterBar,
  Icon,
  Money,
  PageHeader,
  Pagination,
  StatusBadge,
  actionsColumn,
  paginate,
  sortRows,
  useTableState,
  useToast,
  type Column,
} from '~/components';
import { listFamilies } from '~/lib/admin';
import { listPlans } from '~/lib/billing';
import { CURRENCIES, type Currency } from '~/lib/money';
import {
  cancelSubscription,
  getSubscription,
  grantSubscription,
  listSubscriptions,
  type AdminSubscriptionRow,
} from '~/lib/subscriptions';

export const Route = createFileRoute('/_authed/subscriptions')({
  component: SubscriptionsPage,
});

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Past due' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'incomplete', label: 'Incomplete' },
];

function SubscriptionsPage() {
  const table = useTableState({
    defaultSort: { key: 'createdAt', direction: 'desc' },
    filterKeys: ['status', 'kind'],
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const [grantOpen, setGrantOpen] = useState(false);

  const subs = useQuery({
    queryKey: ['subscriptions', table.filters.status ?? 'all'],
    queryFn: ({ signal }) => listSubscriptions(table.filters.status, signal),
  });

  const filtered = useMemo(() => {
    let rows = subs.data ?? [];
    const kind = table.filters.kind;
    if (kind === 'comp') rows = rows.filter((r) => r.isComp);
    else if (kind === 'paid') rows = rows.filter((r) => !r.isComp && !r.isFreePlan);
    else if (kind === 'free') rows = rows.filter((r) => r.isFreePlan);

    const q = table.q.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.ownerLabel.toLowerCase().includes(q) || r.planSlug.toLowerCase().includes(q),
    );
  }, [subs.data, table.q, table.filters.kind]);

  const sorted = useMemo(
    () =>
      sortRows(filtered, table.sort, {
        ownerLabel: (r) => r.ownerLabel,
        planSlug: (r) => r.planSlug,
        status: (r) => r.status,
        currentPeriodEnd: (r) => r.currentPeriodEnd,
        createdAt: (r) => r.createdAt,
      }),
    [filtered, table.sort],
  );

  const pageRows = useMemo(
    () => paginate(sorted, table.page, table.pageSize),
    [sorted, table.page, table.pageSize],
  );

  const columns: Column<AdminSubscriptionRow>[] = [
    {
      key: 'ownerLabel',
      header: 'Customer',
      sortable: true,
      alwaysVisible: true,
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-admin-900">{r.ownerLabel}</p>
          <p className="truncate text-xs text-admin-400">{r.ownerType}</p>
        </div>
      ),
    },
    {
      key: 'planSlug',
      header: 'Plan',
      sortable: true,
      render: (r) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium text-admin-800">{r.planSlug}</span>
          {r.isComp && <StatusBadge status="comp" label="Comp" tone="info" />}
          {r.isFreePlan && <StatusBadge status="free" label="Free" tone="neutral" />}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (r) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={r.status} dot />
          {r.cancelAtPeriodEnd && <StatusBadge status="ending" label="Ending" tone="warning" />}
        </div>
      ),
    },
    {
      key: 'interval',
      header: 'Billing',
      hideBelow: 'md',
      render: (r) => (
        <span className="text-admin-500">
          {r.currency} · {r.interval === 'year' ? 'Yearly' : 'Monthly'}
        </span>
      ),
    },
    {
      key: 'currentPeriodEnd',
      header: 'Renews',
      sortable: true,
      hideBelow: 'md',
      render: (r) =>
        r.currentPeriodEnd ? (
          <span className="text-admin-500">{formatDate(r.currentPeriodEnd)}</span>
        ) : (
          <span className="text-admin-300">—</span>
        ),
    },
    {
      key: 'createdAt',
      header: 'Started',
      sortable: true,
      hideBelow: 'lg',
      render: (r) => <span className="text-admin-500">{formatDate(r.createdAt)}</span>,
    },
    actionsColumn<AdminSubscriptionRow>({
      label: (r) => `subscription for ${r.ownerLabel}`,
      primary: (r) => ({
        id: 'open',
        label: 'Open subscription',
        icon: 'eye',
        onSelect: () => setOpenId(r.id),
      }),
      actions: (r) => [
        {
          id: 'invoices',
          label: 'View invoices',
          icon: 'receipt',
          onSelect: () => window.location.assign(`/payments?q=${encodeURIComponent(r.ownerLabel)}`),
        },
        {
          id: 'copy-id',
          label: 'Copy subscription id',
          icon: 'copy',
          onSelect: () => void navigator.clipboard?.writeText(r.id),
        },
        {
          id: 'cancel',
          label: 'Cancel subscription',
          icon: 'close',
          destructive: true,
          // The drawer owns the cancel dialog — it is where the
          // immediately/period-end choice and the required reason live.
          onSelect: () => setOpenId(r.id),
          disabled: r.status === 'canceled',
          disabledReason: 'This subscription is already canceled.',
        },
      ],
    }),
  ];

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Subscriptions"
        description="Who has access, on what plan, and until when."
        actions={
          <Button type="button" onClick={() => setGrantOpen(true)}>
            Grant a comp
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={pageRows}
        rowKey={(r) => r.id}
        loading={subs.isLoading}
        error={subs.error}
        onRetry={() => void subs.refetch()}
        sort={table.sort}
        onSortChange={table.setSort}
        onRowClick={(r) => setOpenId(r.id)}
        isRowActive={(r) => r.id === openId}
        columnVisibility
        caption="Subscriptions"
        empty={{
          icon: 'card',
          title: table.isFiltered ? 'No subscriptions match these filters' : 'No subscriptions yet',
          description: table.isFiltered
            ? 'Try clearing the filters to see everything.'
            : 'Subscriptions appear here when a family subscribes or is granted a comp.',
        }}
        toolbar={
          <FilterBar
            query={table.q}
            onQueryChange={table.setQuery}
            searchPlaceholder="Search customer or plan…"
            filters={[
              {
                key: 'status',
                label: 'Status',
                value: table.filters.status ?? '',
                options: STATUS_OPTIONS,
              },
              {
                key: 'kind',
                label: 'Kind',
                value: table.filters.kind ?? '',
                options: [
                  { value: 'paid', label: 'Paid' },
                  { value: 'comp', label: 'Comp' },
                  { value: 'free', label: 'Free tier' },
                ],
              },
            ]}
            onFilterChange={table.setFilter}
            onClear={table.clearFilters}
            isFiltered={table.isFiltered}
          />
        }
        footer={
          <Pagination
            page={table.page}
            pageSize={table.pageSize}
            total={sorted.length}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        }
      />

      <SubscriptionDrawer id={openId} onClose={() => setOpenId(null)} />
      <GrantDialog open={grantOpen} onOpenChange={setGrantOpen} />
    </div>
  );
}

function SubscriptionDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const [cancelOpen, setCancelOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['subscriptions', 'detail', id],
    queryFn: ({ signal }) => getSubscription(id!, signal),
    enabled: id != null,
  });

  const sub = detail.data?.subscription;

  return (
    <>
      <Drawer
        open={id != null}
        onOpenChange={(open) => !open && onClose()}
        title="Subscription"
        description={sub?.ownerLabel}
        badge={
          sub && (
            <div className="flex items-center gap-1.5">
              <StatusBadge status={sub.status} dot />
              {sub.isComp && <StatusBadge status="comp" label="Comp" tone="info" />}
            </div>
          )
        }
        width="lg"
        footer={
          sub && sub.status !== 'canceled' ? (
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-error-500 px-3.5 py-2 text-sm font-semibold text-error-700 transition-colors hover:bg-error-50"
            >
              <Icon name="close" className="h-4 w-4" />
              Cancel subscription
            </button>
          ) : null
        }
      >
        {detail.isLoading && <div className="h-40 animate-pulse rounded-xl bg-admin-100" />}

        {sub && (
          <>
            <DetailSection title="Plan">
              <DetailRow label="Plan">{sub.planSlug}</DetailRow>
              <DetailRow label="Tier">{sub.planTier}</DetailRow>
              <DetailRow label="Kid limit">{sub.maxKids ?? 'unlimited'}</DetailRow>
              <DetailRow label="Billing">
                {sub.currency} · {sub.interval === 'year' ? 'Yearly' : 'Monthly'}
              </DetailRow>
            </DetailSection>

            <DetailSection title="Period">
              <DetailRow label="Started">{formatDate(sub.createdAt)}</DetailRow>
              <DetailRow label="Current from">
                {sub.currentPeriodStart ? formatDate(sub.currentPeriodStart) : '—'}
              </DetailRow>
              <DetailRow label="Renews">
                {sub.currentPeriodEnd ? formatDate(sub.currentPeriodEnd) : '—'}
                {sub.cancelAtPeriodEnd && (
                  <span className="ms-2 text-xs text-warning-700">ends, will not renew</span>
                )}
              </DetailRow>
              {sub.canceledAt && (
                <DetailRow label="Canceled">{formatDate(sub.canceledAt)}</DetailRow>
              )}
            </DetailSection>

            <DetailSection title="Provenance">
              <DetailRow label="Provider">{sub.provider}</DetailRow>
              <DetailRow label="Provider ref">
                {sub.providerRef ? (
                  <code className="break-all text-xs">{sub.providerRef}</code>
                ) : (
                  <span className="text-admin-400">none (nothing was charged)</span>
                )}
              </DetailRow>
              {sub.grantedBy && <DetailRow label="Granted by">{sub.grantedBy}</DetailRow>}
            </DetailSection>

            <DetailSection title={`Cards (${detail.data?.paymentMethods.length ?? 0})`}>
              {(detail.data?.paymentMethods.length ?? 0) === 0 ? (
                <p className="py-2 text-sm text-admin-400">No card on file.</p>
              ) : (
                detail.data!.paymentMethods.map((m) => (
                  <DetailRow key={m.id} label={m.brand ?? 'Card'}>
                    <span className="tabular-nums">•••• {m.last4 ?? '????'}</span>
                    {m.expMonth && m.expYear && (
                      <span className="ms-2 text-xs text-admin-400">
                        exp {String(m.expMonth).padStart(2, '0')}/{m.expYear}
                      </span>
                    )}
                    {m.isDefault && (
                      <span className="ms-2 text-xs font-medium text-brand-600">default</span>
                    )}
                  </DetailRow>
                ))
              )}
            </DetailSection>

            <DetailSection title={`Invoices (${detail.data?.invoices.length ?? 0})`}>
              {(detail.data?.invoices.length ?? 0) === 0 ? (
                <p className="py-2 text-sm text-admin-400">No invoices — nothing was charged.</p>
              ) : (
                detail.data!.invoices.map((i) => (
                  <DetailRow key={i.id} label={<StatusBadge status={i.status} />}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-xs text-admin-500">{formatDate(i.issuedAt)}</span>
                      <Money amountMinor={i.amountMinor} currency={i.currency} />
                    </div>
                  </DetailRow>
                ))
              )}
            </DetailSection>
          </>
        )}
      </Drawer>

      {sub && (
        <CancelDialog
          open={cancelOpen}
          onOpenChange={setCancelOpen}
          subscriptionId={sub.id}
          ownerLabel={sub.ownerLabel}
        />
      )}
    </>
  );
}

function CancelDialog({
  open,
  onOpenChange,
  subscriptionId,
  ownerLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriptionId: string;
  ownerLabel: string;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [immediately, setImmediately] = useState(false);
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => cancelSubscription(subscriptionId, { immediately, reason: reason.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscriptions'] });
      toast(immediately ? 'Subscription canceled immediately' : 'Will not renew', {
        tone: 'success',
      });
    },
  });

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) {
          setImmediately(false);
          setReason('');
        }
      }}
      title="Cancel this subscription"
      description={
        <>
          For <span className="font-medium">{ownerLabel}</span>.{' '}
          {immediately
            ? 'Access ends right now, even though the period is paid for.'
            : 'Access continues until the end of the current period, then stops.'}
        </>
      }
      confirmLabel={immediately ? 'Cancel now' : 'Stop renewal'}
      tone="danger"
      // Ending paid-for access early is the destructive branch, so it gets the
      // typed confirmation; letting a period run out does not.
      requireTyped={immediately ? 'CANCEL NOW' : undefined}
      disabled={reason.trim() === ''}
      onConfirm={async () => {
        await mutation.mutateAsync();
      }}
    >
      <div className="space-y-3">
        <label className="flex items-start gap-2.5 rounded-lg border border-admin-200 p-3">
          <input
            type="checkbox"
            checked={immediately}
            onChange={(e) => setImmediately(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-admin-300 accent-error-500"
          />
          <span className="text-sm">
            <span className="font-medium text-admin-800">End access immediately</span>
            <span className="mt-0.5 block text-xs text-admin-500">
              Cuts off a period the customer has already paid for. Leave unchecked to let it run
              out.
            </span>
          </span>
        </label>

        <div>
          <label htmlFor="cancel-reason" className="block text-sm font-medium text-admin-700">
            Reason
          </label>
          <textarea
            id="cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why is this being canceled?"
            className="mt-1.5 w-full rounded-lg border border-admin-200 px-3 py-2 text-sm text-admin-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
          <p className="mt-1 text-xs text-admin-400">Required. Stored in the audit log.</p>
        </div>
      </div>
    </ConfirmDialog>
  );
}

/**
 * Comp a subscription to a family.
 *
 * 🔴 A COMP IS AN ORDINARY SUBSCRIPTION. The API writes an `active` row with
 * `metadata.comp`, `granted_by_user_id`, and no `provider_ref`. It entitles the
 * family because it is ACTIVE — not because anything anywhere checks `comp`.
 * The `Comp` badge in the table is a label, never a behaviour switch, and there
 * is deliberately no `if (comp)` branch in the entitlement helper. That is what
 * keeps comps safe: a comp expires down exactly the same path as a paid one.
 *
 * ⚠️ Granting to a family that already has a subscription REPLACES it — the API
 * reuses the existing row so a family never holds two and `activeSubscription`
 * stays unambiguous. The dialog says so out loud when that is what will happen.
 */
function GrantDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const [familyId, setFamilyId] = useState('');
  const [planId, setPlanId] = useState('');
  const [currency, setCurrency] = useState<Currency>('QAR');
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [days, setDays] = useState('30');
  const [reason, setReason] = useState('');
  const [familyQuery, setFamilyQuery] = useState('');

  // Both lists share their query keys with the pages that own them, so opening
  // this dialog usually costs no fetch at all.
  const families = useQuery({
    queryKey: ['families'],
    queryFn: ({ signal }) => listFamilies(signal),
    enabled: open,
  });
  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: ({ signal }) => listPlans(signal),
    enabled: open,
  });

  // Only FAMILY plans can be comped — the API rejects a school plan with
  // `invalid_input`, and schools are dark for MVP anyway. Filtering here means
  // the operator is never offered something the server will refuse.
  const familyPlans = useMemo(
    () => (plans.data ?? []).filter((p) => p.audience === 'family' && p.status !== 'archived'),
    [plans.data],
  );

  const matchingFamilies = useMemo(() => {
    const list = families.data ?? [];
    const q = familyQuery.trim().toLowerCase();
    if (!q) return list.slice(0, 50);
    return list.filter((f) => f.parentLabel.toLowerCase().includes(q)).slice(0, 50);
  }, [families.data, familyQuery]);

  const selectedFamily = useMemo(
    () => (families.data ?? []).find((f) => f.id === familyId) ?? null,
    [families.data, familyId],
  );

  const dayCount = Number(days);
  const daysValid = Number.isInteger(dayCount) && dayCount > 0 && dayCount <= 3650;
  const valid = familyId !== '' && planId !== '' && daysValid && reason.trim() !== '';

  // Shown before confirming, so "30 days" is never left abstract.
  const endsOn = daysValid
    ? new Date(Date.now() + dayCount * 86_400_000).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : null;

  const mutation = useMutation({
    mutationFn: () =>
      grantSubscription({
        familyId,
        planId,
        currency,
        interval,
        days: dayCount,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscriptions'] });
      void qc.invalidateQueries({ queryKey: ['families'] });
    },
  });

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) {
          setFamilyId('');
          setPlanId('');
          setCurrency('QAR');
          setInterval('month');
          setDays('30');
          setReason('');
          setFamilyQuery('');
        }
      }}
      title="Grant a comp subscription"
      description="Gives a family paid-tier access without charging them. It becomes an ordinary active subscription and expires on its own — nothing has to be undone later."
      confirmLabel="Grant comp"
      tone="primary"
      disabled={!valid || mutation.isPending}
      onConfirm={async () => {
        await mutation.mutateAsync();
        toast(
          `Comped ${selectedFamily?.parentLabel ?? 'family'} for ${dayCount} day${
            dayCount === 1 ? '' : 's'
          }`,
          { tone: 'success' },
        );
      }}
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="grant-family" className="block text-sm font-medium text-admin-700">
            Family
          </label>
          {families.isError ? (
            // Never render an empty picker on a failed query — that is
            // indistinguishable from "there are no families".
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-sm text-error-600">Could not load families.</span>
              <button
                type="button"
                onClick={() => void families.refetch()}
                className="text-sm font-semibold text-brand-600 underline"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              <Input
                id="grant-family"
                value={familyQuery}
                onChange={(e) => setFamilyQuery(e.target.value)}
                placeholder="Search by parent…"
                className="mt-1.5"
                autoComplete="off"
              />
              <select
                value={familyId}
                onChange={(e) => setFamilyId(e.target.value)}
                disabled={families.isLoading}
                aria-label="Family"
                size={5}
                className="mt-1.5 w-full rounded-lg border border-admin-200 bg-white p-1 text-sm text-admin-900 disabled:opacity-50"
              >
                {matchingFamilies.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.parentLabel} · {f.kidCount} kid{f.kidCount === 1 ? '' : 's'}
                    {f.subscriptionStatus ? ` · ${f.subscriptionStatus}` : ''}
                  </option>
                ))}
              </select>
              {!families.isLoading && matchingFamilies.length === 0 && (
                <p className="mt-1 text-xs text-admin-400">No family matches that search.</p>
              )}
            </>
          )}
        </div>

        {/* The single most important thing to say before confirming: this is a
            replacement, not an addition. A family holds at most one row. */}
        {selectedFamily?.subscriptionStatus && (
          <p className="flex items-start gap-1.5 rounded-lg bg-warning-50 px-3 py-2 text-xs leading-relaxed text-warning-700">
            <Icon name="alert" className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>
              This family already has a <strong>{selectedFamily.subscriptionStatus}</strong>{' '}
              subscription. Granting a comp REPLACES it — a family never holds two.
            </span>
          </p>
        )}

        <div>
          <label htmlFor="grant-plan" className="block text-sm font-medium text-admin-700">
            Plan
          </label>
          {plans.isError ? (
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-sm text-error-600">Could not load plans.</span>
              <button
                type="button"
                onClick={() => void plans.refetch()}
                className="text-sm font-semibold text-brand-600 underline"
              >
                Retry
              </button>
            </div>
          ) : (
            <select
              id="grant-plan"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              disabled={plans.isLoading}
              className="mt-1.5 h-10 w-full rounded-lg border border-admin-200 bg-white px-2.5 text-sm text-admin-900 disabled:opacity-50"
            >
              <option value="">Choose a plan…</option>
              {familyPlans.map((p) => (
                // slug, never nameKey — nameKey is a raw translation key.
                <option key={p.id} value={p.id}>
                  {p.slug}
                  {p.maxKids != null ? ` · up to ${p.maxKids} kids` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="grant-days" className="block text-sm font-medium text-admin-700">
              Length (days)
            </label>
            <Input
              id="grant-days"
              type="number"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="mt-1.5"
              aria-invalid={days !== '' && !daysValid}
            />
            {endsOn ? (
              <p className="mt-1 text-xs text-admin-500">Access ends {endsOn}.</p>
            ) : (
              <p role="alert" className="mt-1 text-xs text-error-600">
                Between 1 and 3650 days.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="grant-interval" className="block text-sm font-medium text-admin-700">
              Billing shape
            </label>
            <select
              id="grant-interval"
              value={interval}
              onChange={(e) => setInterval(e.target.value as 'month' | 'year')}
              className="mt-1.5 h-10 w-full rounded-lg border border-admin-200 bg-white px-2.5 text-sm text-admin-900"
            >
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
            {/* Nothing is charged, so these two fields only describe the row.
                Saying so stops an operator agonising over a field that cannot
                cost anyone money. */}
            <p className="mt-1 text-xs text-admin-400">Recorded only — nothing is charged.</p>
          </div>
        </div>

        <div>
          <label htmlFor="grant-currency" className="block text-sm font-medium text-admin-700">
            Currency
          </label>
          <select
            id="grant-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
            className="mt-1.5 h-10 w-full rounded-lg border border-admin-200 bg-white px-2.5 text-sm text-admin-900"
          >
            {CURRENCIES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="grant-reason" className="block text-sm font-medium text-admin-700">
            Reason
          </label>
          <textarea
            id="grant-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Why is this being comped?"
            className="mt-1.5 w-full rounded-lg border border-admin-200 px-3 py-2 text-sm text-admin-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
          <p className="mt-1 text-xs text-admin-400">Required. Stored in the audit log.</p>
        </div>
      </div>
    </ConfirmDialog>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
